import { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { BookOpen, Zap, CreditCard, Copy, Check, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { ANTHROPIC_PROXY_URL } from "@/const";

const TOC = [
  {
    id: "quickstart", icon: <Zap size={14} />, labelZh: "快速开始", labelEn: "Quickstart",
    children: [
      { id: "qs-install", labelZh: "安装 SDK", labelEn: "Install SDK" },
      { id: "qs-apikey", labelZh: "设置 API Key", labelEn: "Set API Key" },
      { id: "qs-first-call", labelZh: "第一次调用", labelEn: "First API Call" },
    ],
  },
  {
    id: "pricing", icon: <CreditCard size={14} />, labelZh: "定价", labelEn: "Pricing",
    children: [
      { id: "pricing-st", labelZh: "ScienceToken (ST)", labelEn: "ScienceToken (ST)" },
    ],
  },
];

function CodeSnippet({ code, label }: { code: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="code-block my-4 overflow-hidden">
      {label && (
        <div className="px-4 py-2 border-b border-white/10 flex items-center justify-between">
          <span className="text-xs text-slate-500">{label}</span>
          <button onClick={() => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
            className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1">
            {copied ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      )}
      <pre className="p-4 overflow-x-auto text-xs leading-relaxed font-mono text-slate-300"><code>{code}</code></pre>
    </div>
  );
}

export default function Docs() {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState("quickstart");

  useEffect(() => {
    const ids = TOC.flatMap((s) => [s.id, ...s.children.map((c) => c.id)]);
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveTab(entry.target.id);
          }
        }
      },
      { rootMargin: "-100px 0px -60% 0px", threshold: 0 }
    );
    ids.forEach((id) => { const el = document.getElementById(id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const isZh = language === "zh";
  const allIds = TOC.flatMap((s) => [s.id, ...s.children.map((c) => c.id)]);

  return (
    <div className="min-h-screen bg-[var(--page-bg)] text-foreground">
      <Navbar />

      <div className="flex max-w-[1280px] mx-auto pt-16">
        {/* Left Sidebar Nav */}
        <aside className="hidden lg:block w-[220px] flex-shrink-0 sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto py-8 pl-6 pr-4 border-r border-border">
          <div className="flex items-center gap-2 mb-6">
            <BookOpen size={16} className="text-blue-400" />
            <span className="text-sm font-semibold">{isZh ? "开发者文档" : "Docs"}</span>
          </div>
          <nav className="space-y-4">
            {TOC.map((section) => (
              <div key={section.id}>
                <button
                  onClick={() => scrollTo(section.id)}
                  className={cn(
                    "flex items-center gap-1.5 text-sm font-medium mb-1.5 transition-colors w-full text-left",
                    activeTab === section.id ? "text-blue-500" : "text-foreground hover:text-blue-500"
                  )}
                >
                  {section.icon}
                  {isZh ? section.labelZh : section.labelEn}
                </button>
                <div className="ml-5 space-y-1 border-l border-border pl-3">
                  {section.children.map((child) => (
                    <button
                      key={child.id}
                      onClick={() => scrollTo(child.id)}
                      className={cn(
                        "block text-xs w-full text-left py-0.5 transition-colors",
                        activeTab === child.id ? "text-blue-500 font-medium" : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {isZh ? child.labelZh : child.labelEn}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 px-6 sm:px-10 py-10 max-w-[860px] space-y-16">

        {/* ═══ Quickstart ═══ */}
        <section id="quickstart">
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {isZh ? "快速开始" : "Quickstart"}
          </h2>

          <div className="space-y-8">
            <div>
              <h3 id="qs-install" className="text-lg font-semibold mb-3">{isZh ? "1. 安装 SDK" : "1. Install SDK"}</h3>
              <p className="text-slate-400 mb-3">
                {isZh
                  ? "ScienceToken 使用 Anthropic SDK 格式，安装以下依赖即可开始。"
                  : "ScienceToken uses the Anthropic SDK format. Install the SDK to get started."}
              </p>
              <CodeSnippet label="Python" code="pip install anthropic" />
              <CodeSnippet label="Node.js" code="npm install @anthropic-ai/sdk" />
            </div>

            <div>
              <h3 id="qs-apikey" className="text-lg font-semibold mb-3">{isZh ? "2. 设置 API Key" : "2. Set API Key"}</h3>
              <CodeSnippet label={isZh ? "环境变量" : "Environment Variable"} code={`# .env\nSCIENCETOKEN_API_KEY=sk-st-your-key-here\nSCIENCETOKEN_BASEURL=${ANTHROPIC_PROXY_URL}\n\n# Shell\nexport SCIENCETOKEN_API_KEY=sk-st-your-key-here\nexport SCIENCETOKEN_BASEURL=${ANTHROPIC_PROXY_URL}`} />
            </div>

            <div>
              <h3 id="qs-first-call" className="text-lg font-semibold mb-3">{isZh ? "3. 第一次调用" : "3. First API Call"}</h3>
              <p className="text-slate-400 mb-3">
                {isZh
                  ? "将 base_url 指向 ScienceToken，使用 Anthropic Messages API 格式调用所有科学模型。"
                  : "Point base_url to ScienceToken and use the Anthropic Messages API format to access all scientific models."}
              </p>
              <CodeSnippet label="Python" code={`import anthropic\nimport os\n\nclient = anthropic.Anthropic(\n    api_key=os.environ["SCIENCETOKEN_API_KEY"],\n    base_url=os.environ["SCIENCETOKEN_BASEURL"]\n)\n\nmessage = client.messages.create(\n    model="xtalpi-science-standard",\n    max_tokens=1024,\n    messages=[{\n        "role": "user",\n        "content": "Predict the logP of aspirin: CC(=O)Oc1ccccc1C(=O)O"\n    }]\n)\n\nprint(message)`} />
              <CodeSnippet label="cURL" code={`curl "${ANTHROPIC_PROXY_URL}/v1/messages" \\\n  -H "x-api-key: $SCIENCETOKEN_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -H "anthropic-version: 2023-06-01" \\\n  -d '{\n    "model": "xtalpi-science-standard",\n    "max_tokens": 1024,\n    "messages": [{\n      "role": "user",\n      "content": "Predict logP for: CC(=O)Oc1ccccc1C(=O)O"\n    }]\n  }'`} />
            </div>
          </div>
        </section>

        {/* ═══ Pricing ═══ */}
        <section id="pricing">
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {isZh ? "定价" : "Pricing"}
          </h2>

          <div className="space-y-8">
            <div className="sci-card p-5">
              <h3 id="pricing-st" className="text-lg font-semibold mb-2">ScienceToken (ST)</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                {isZh
                  ? "ScienceToken (ST) 是平台统一计费货币。所有服务（LLM 推理、计算任务、数据库查询）均以 ST 计费。"
                  : "ScienceToken (ST) is the unified billing currency. All services (LLM inference, compute jobs, database queries) are billed in ST."}
              </p>
              <div className="mt-3 text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                1 ST ≈ $1 USD
              </div>
              <p className="mt-2 text-xs text-slate-500 leading-relaxed">
                {isZh
                  ? "约定俗成的换算关系：模型的 st_per_ktok 与 cost_per_ktok（USD/Ktok）通常配置成相同数值，因此单次调用扣 ST 数 ≡ 上游真实 USD 成本。管理员可设为非 1:1 以支撑利润 / 补贴等场景。"
                  : "Conventional mapping: each model's st_per_ktok is typically set to the same value as its upstream cost_per_ktok (USD/Ktok), so the ST charged per call equals the upstream USD cost. Admins may configure a non-1:1 ratio for markup / subsidy scenarios."}
              </p>
            </div>
          </div>
        </section>

        </main>
      </div>

      <Footer />
    </div>
  );
}
